import React, { useState, useEffect } from 'react';
import {
  Container, Card, CardContent, Typography, TextField, Button, Box,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert,
  ToggleButtonGroup, ToggleButton, Switch
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { isAdmin } from '../api';
import { useLanguage } from '../i18n/LanguageContext';
import { useColorMode } from '../theme/ColorModeContext';

const ProfileSetup: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { t, lang, setLanguage } = useLanguage();
  const { mode, toggleColorMode } = useColorMode();
  const userIsAdmin = isAdmin();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [height, setHeight] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [activityLevel, setActivityLevel] = useState('sedentary');
  const [targetWeight, setTargetWeight] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [country, setCountry] = useState('');
  const [dietPlan, setDietPlan] = useState('standard');
  const [fastingDays, setFastingDays] = useState<string[]>([]);

  useEffect(() => {
    api.get('/profile')
      .then(response => {
        const p = response.data;
        setName(p.name || '');
        setEmail(p.email || '');
        setHeight(p.height?.toString() || '');
        setDateOfBirth(p.date_of_birth || '');
        setGender(p.gender || '');
        setActivityLevel(p.activity_level || 'sedentary');
        setCountry(p.country || '');
        setDietPlan(p.diet_plan || 'standard');
        setFastingDays(p.fasting_days ? p.fasting_days.split(',') : []);
        setTargetWeight(p.target_weight?.toString() || '');
        setTargetDate(p.target_date || '');
      })
      .catch(() => setError(t.profileFailed))
      .finally(() => setLoading(false));
  }, [t.profileFailed]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/update_profile', {
        name,
        height: parseFloat(height),
        date_of_birth: dateOfBirth,
        gender,
        activity_level: activityLevel,
        country,
        diet_plan: dietPlan,
        fasting_days: fastingDays.join(','),
        target_weight: parseFloat(targetWeight),
        target_date: targetDate,
      });
      setSuccess(t.profileUpdated);
    } catch {
      setError(t.profileFailed);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await api.post('/logout');
    } catch {
      // Ignore errors — proceed with local logout regardless
    }
    localStorage.removeItem('token');
    sessionStorage.clear();
    navigate('/login');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">{t.profile}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'dark' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
          <Switch
            size="small"
            checked={mode === 'dark'}
            onChange={toggleColorMode}
            inputProps={{ 'aria-label': t.darkMode }}
          />
          <ToggleButtonGroup
            value={lang}
            exclusive
            onChange={(_, v) => v && setLanguage(v)}
            size="small"
          >
            <ToggleButton value="sv" sx={{ px: 1.5, py: 0.25, fontSize: 12 }}>SV</ToggleButton>
            <ToggleButton value="en" sx={{ px: 1.5, py: 0.25, fontSize: 12 }}>EN</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {userIsAdmin && (
        <Button
          variant="outlined"
          startIcon={<AdminPanelSettingsIcon />}
          fullWidth
          sx={{ mb: 2 }}
          onClick={() => navigate('/admin/users')}
        >
          {t.adminPortal}
        </Button>
      )}

      <Card sx={{ borderRadius: 4 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth size="small" label={t.name} value={name}
              onChange={e => setName(e.target.value)} sx={{ mb: 2 }}
            />
            <TextField
              fullWidth size="small" label={t.email} value={email}
              disabled sx={{ mb: 2 }}
            />
            <TextField
              fullWidth size="small" label={t.height} type="number"
              value={height} onChange={e => setHeight(e.target.value)} sx={{ mb: 2 }}
            />
            <TextField
              fullWidth size="small" label={t.dateOfBirth} type="date"
              value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
            />
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{t.gender}</InputLabel>
              <Select value={gender} label={t.gender} onChange={e => setGender(e.target.value)}>
                <MenuItem value="male">{t.male}</MenuItem>
                <MenuItem value="female">{t.female}</MenuItem>
                <MenuItem value="other">{t.other}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{t.activityLevel}</InputLabel>
              <Select value={activityLevel} label={t.activityLevel} onChange={e => setActivityLevel(e.target.value)}>
                <MenuItem value="sedentary">{t.actSedentary}</MenuItem>
                <MenuItem value="light">{t.actLight}</MenuItem>
                <MenuItem value="moderate">{t.actModerate}</MenuItem>
                <MenuItem value="active">{t.actActive}</MenuItem>
                <MenuItem value="very_active">{t.actVeryActive}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{t.country}</InputLabel>
              <Select value={country} label={t.country} onChange={e => setCountry(e.target.value)}>
                <MenuItem value="">{t.countryAll}</MenuItem>
                <MenuItem value="se">Sverige / Sweden</MenuItem>
                <MenuItem value="no">Norge / Norway</MenuItem>
                <MenuItem value="dk">Danmark / Denmark</MenuItem>
                <MenuItem value="fi">Suomi / Finland</MenuItem>
                <MenuItem value="de">Deutschland / Germany</MenuItem>
                <MenuItem value="gb">UK</MenuItem>
                <MenuItem value="us">USA</MenuItem>
                <MenuItem value="fr">France</MenuItem>
                <MenuItem value="es">España / Spain</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{t.dietPlan}</InputLabel>
              <Select value={dietPlan} label={t.dietPlan} onChange={e => { setDietPlan(e.target.value); if (e.target.value !== '5_2') setFastingDays([]); }}>
                <MenuItem value="standard">{t.dietStandard}</MenuItem>
                <MenuItem value="5_2">{t.diet52}</MenuItem>
              </Select>
            </FormControl>
            {dietPlan === '5_2' && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{t.selectFastingDays}</InputLabel>
                <Select
                  multiple
                  value={fastingDays}
                  label={t.selectFastingDays}
                  onChange={e => {
                    const val = e.target.value;
                    setFastingDays(typeof val === 'string' ? val.split(',') : val);
                  }}
                  renderValue={(selected) => {
                    const dayLabels: Record<string, string> = { '0': t.monday, '1': t.tuesday, '2': t.wednesday, '3': t.thursday, '4': t.friday, '5': t.saturday, '6': t.sunday };
                    return (selected as string[]).map(d => dayLabels[d] || d).join(', ');
                  }}
                >
                  {[
                    { value: '0', label: t.monday },
                    { value: '1', label: t.tuesday },
                    { value: '2', label: t.wednesday },
                    { value: '3', label: t.thursday },
                    { value: '4', label: t.friday },
                    { value: '5', label: t.saturday },
                    { value: '6', label: t.sunday },
                  ].map(day => (
                    <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              fullWidth size="small" label={t.targetWeight} type="number"
              value={targetWeight} onChange={e => setTargetWeight(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth size="small" label={t.targetDate} type="date"
              value={targetDate} onChange={e => setTargetDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
            />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Button
              type="submit" variant="contained" fullWidth
              disabled={saving}
            >
              {saving ? t.saving : t.saveProfile}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Button
        variant="outlined"
        color="error"
        fullWidth
        sx={{ mt: 2 }}
        onClick={handleLogout}
        disabled={loggingOut}
      >
        {loggingOut ? <CircularProgress size={22} color="inherit" /> : t.logOut}
      </Button>
    </Container>
  );
};

export default ProfileSetup;
